import { useState } from 'react';

import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import NonFieldErrors from '@perpay-web/components/base/NonFieldErrors/NonFieldErrors';
import LegacyFocusLayout from '@perpay-web/components/composite/LegacyFocusLayout/LegacyFocusLayout';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import ResponsiveCopy from '@perpay-web/fintech/components/base/ResponsiveCopy/ResponsiveCopy';
import ResponsiveHeader from '@perpay-web/fintech/components/base/ResponsiveHeader/ResponsiveHeader';
import CardUploadDocumentSection from '@perpay-web/fintech/components/composite/CardUploadDocumentSection/CardUploadDocumentSection';
import { paths } from '@perpay-web/fintech/props/appPaths';

import styles from './CardUploadDocuments.scss';

const getErrorMessageForSection = (errors, validationError) =>
    errors.length ? errors[0] : validationError;

const getCopy = (isProofOfIdRequested, isProofOfAddressRequested) => {
    if (isProofOfIdRequested && !isProofOfAddressRequested) {
        return 'To complete review of your application for the Perpay Credit Card, please upload a Photo ID to verify your identity:';
    }
    if (!isProofOfIdRequested && isProofOfAddressRequested) {
        return 'To complete review of your application for the Perpay Credit Card, please upload a recent proof of address to verify your identity:';
    }
    return 'To complete review of your application for the Perpay Credit Card, please upload the following documents to verify your identity:';
};

const CardUploadDocuments = ({
    onClickSubmit,
    analyticsOnClickUploadPhotoId,
    analyticsOnClickUploadProofOfAddress,
    isProofOfIdRequested,
    isProofOfAddressRequested,
    isSubmitting,
    errors = {},
    dashboardURL = paths.dashboard.path,
}) => {
    const [proofOfIdFile, setProofOfIdFile] = useState(null);
    const [proofOfAddressFile, setProofOfAddressFile] = useState(null);

    const [proofOfIdFileValidationError, setProofOfIdFileValidationError] =
        useState(null);
    const [
        proofOfAddressFileValidationError,
        setProofOfAddressFileValidationError,
    ] = useState(null);

    const proofOfIdSubmissionErrors = errors.proofOfIdFile || [];
    const proofOfAddressSubmissionErrors = errors.proofOfAddressFile || [];

    const copy = getCopy(isProofOfIdRequested, isProofOfAddressRequested);

    // disable submit if required docs not submitted or files invalid
    const disableSubmit =
        (isProofOfIdRequested && !proofOfIdFile) ||
        (isProofOfAddressRequested && !proofOfAddressFile) ||
        proofOfIdFileValidationError ||
        proofOfAddressFileValidationError;

    return (
        <LegacyFocusLayout
            className={styles.container}
            footer={
                <>
                    <CardCta
                        Component={CustomButton}
                        onClick={() =>
                            onClickSubmit({ proofOfIdFile, proofOfAddressFile })
                        }
                        className={styles['footer-button']}
                        loading={isSubmitting}
                        disabled={disableSubmit}
                    >
                        Submit
                    </CardCta>
                    <CustomButton
                        action='link'
                        href={dashboardURL}
                        size='small'
                    >
                        Dashboard
                    </CustomButton>
                </>
            }
        >
            <div className={styles['inner-container']}>
                <ResponsiveHeader className={styles.header}>
                    We need more information
                </ResponsiveHeader>
                <ResponsiveCopy className={styles.copy}>{copy}</ResponsiveCopy>
                {isProofOfIdRequested ? (
                    <CardUploadDocumentSection
                        title='Photo ID'
                        subtitle='Must not be expired'
                        copy='Drivers License, State ID, Passport or Permanent Resident Card (Green Card)'
                        icon='profile'
                        file={proofOfIdFile}
                        onSelectFile={setProofOfIdFile}
                        onClickUpload={analyticsOnClickUploadPhotoId}
                        error={getErrorMessageForSection(
                            proofOfIdSubmissionErrors,
                            proofOfIdFileValidationError,
                        )}
                        onError={setProofOfIdFileValidationError}
                    />
                ) : null}
                {isProofOfAddressRequested ? (
                    <CardUploadDocumentSection
                        title='Proof of Address'
                        subtitle='Recent (within 30 days)'
                        copy='Utility bill, insurance statement, USPS Change of Address form, bank or credit card statement'
                        icon='house'
                        file={proofOfAddressFile}
                        onSelectFile={setProofOfAddressFile}
                        onClickUpload={analyticsOnClickUploadProofOfAddress}
                        error={getErrorMessageForSection(
                            proofOfAddressSubmissionErrors,
                            proofOfAddressFileValidationError,
                        )}
                        onError={setProofOfAddressFileValidationError}
                    />
                ) : null}
                <NonFieldErrors errors={errors} />
            </div>
        </LegacyFocusLayout>
    );
};

export default CardUploadDocuments;
